import { defineMutator } from '@rocicorp/zero';
import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import * as z from 'zod';
import { zql } from '../schema.js';
import { titleCase } from '../util/index.js';
import { Address } from '../util/schemas.js';
import { assertCan, assertIsAuthenticated, EventLogs } from '../utils.js';
import { addEventLog, DeleteResourceSchema, UpdateResourceStatusSchema } from './util.js';
import { createAddress, updateAddress } from './utils/address-utils.js';
export const UserSectionSchema = z.object({
    id: z.uuidv7(),
    locationId: z.uuidv7(),
    wards: z.array(z.uuidv7()),
});
const UserSchema = z.object({
    username: z.string(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    name: z.string(),
    password: z.string(),
    title: z.string().optional(),
    hospitalSystemId: z.string().optional(),
    nationalPhysicianNumber: z.string().optional(),
    hospitalPhysicianNumber: z.string().optional(),
    isActiveOnCypro: z.boolean().optional(),
    address: Address.optional(),
});
export const AddUserSchema = z.object({
    user: UserSchema,
    groups: z.array(z.uuidv7()),
    sections: z.array(z.object({ id: z.uuidv7(), locationId: z.uuidv7(), wards: z.array(z.uuidv7()) })),
});
export const UpdateUserSchema = AddUserSchema.partial()
    .omit({ user: true })
    .extend({ user: UserSchema.partial().extend({ id: z.uuidv7() }) });
const MIN_PASSWORD_LENGTH = 8;
export const SetupPasswordSchema = z
    .object({
    password: z.string().min(MIN_PASSWORD_LENGTH),
    confirmPassword: z.string(),
})
    .refine(data => data.password === data.confirmPassword, {
    path: ['confirmPassword'],
    error: 'auth:passwords_do_not_match',
});
async function findById(tx, userId) {
    return tx.run(zql.user.where('id', userId).one());
}
export const users = {
    add: defineMutator(AddUserSchema, async ({ ctx }) => {
        assertCan(ctx, 'users.manage');
        // logic is defined on the server
    }),
    update: defineMutator(UpdateUserSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'users.manage');
        const prev = await findById(tx, args.user.id);
        if (!prev)
            throw new Error('Not Found');
        if (args.user.username) {
            const existingUsername = await tx.run(zql.user
                .where(({ and, cmp }) => and(cmp('id', '!=', args.user.id), cmp('username', '=', args.user.username)))
                .one());
            if (existingUsername)
                throw new Error('Username is already taken');
        }
        if (args.user.email) {
            const existingEmail = await tx.run(zql.user
                .where(({ and, cmp }) => and(cmp('id', '!=', args.user.id), cmp('email', '=', args.user.email)))
                .one());
            if (existingEmail)
                throw new Error('Email is already taken');
        }
        const now = Date.now();
        // all-empty address means the user cleared the field: remove the link instead of writing an empty row
        let addressId;
        if (args.user.address) {
            const isEmptyAddress = Object.values(args.user.address).every(v => !v?.trim()?.length);
            if (isEmptyAddress) {
                addressId = null;
            }
            else if (prev.addressId) {
                await updateAddress(tx, prev.addressId, args.user.address, ctx.user.id);
                addressId = prev.addressId;
            }
            else {
                const created = await createAddress(tx, args.user.address, ctx.user.id);
                addressId = created.id;
            }
        }
        const updatedUser = {
            id: args.user.id,
            name: args.user.name,
            firstName: titleCase(args.user.firstName),
            lastName: titleCase(args.user.lastName),
            username: args.user.username?.toLowerCase?.(),
            email: args.user.email,
            title: args.user.title !== undefined ? args.user.title.trim() || null : prev.title,
            hospitalSystemId: args.user.hospitalSystemId !== undefined ? args.user.hospitalSystemId.trim() || null : prev.hospitalSystemId,
            nationalPhysicianNumber: args.user.nationalPhysicianNumber !== undefined
                ? args.user.nationalPhysicianNumber.trim() || null
                : prev.nationalPhysicianNumber,
            hospitalPhysicianNumber: args.user.hospitalPhysicianNumber !== undefined
                ? args.user.hospitalPhysicianNumber.trim() || null
                : prev.hospitalPhysicianNumber,
            isActiveOnCypro: args.user.isActiveOnCypro ?? prev.isActiveOnCypro,
            addressId: addressId ?? prev.addressId,
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        await tx.mutate.user.update(updatedUser);
        if (tx.location === 'server') {
            const patches = createPatch(prev, updatedUser);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: 'user',
                resourceId: updatedUser.id,
                domain: 'configuration',
                event: EventLogs.USER_UPDATED,
                action: 'update',
                patches: patches,
                performedBy: ctx.user.id,
                performedAt: now,
                details: {
                    id: updatedUser.id,
                    username: updatedUser.username,
                },
            }, ctx);
        }
    }),
    setupPassword: defineMutator(SetupPasswordSchema, async ({ ctx }) => {
        assertIsAuthenticated(ctx);
        // logic is implemented on the server
    }),
    updateStatus: defineMutator(UpdateResourceStatusSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'users.manage');
        const prev = await findById(tx, args.id);
        if (!prev)
            throw new Error('Not found');
        const updatedUser = {
            id: args.id,
            status: args.status,
            updatedAt: Date.now(),
            updatedBy: ctx.user.id,
        };
        await tx.mutate.user.update(updatedUser);
        if (tx.location === 'server') {
            const patches = createPatch(prev, updatedUser);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: 'user',
                resourceId: args.id,
                domain: 'configuration',
                action: 'update',
                event: args.status === 'inactive' ? EventLogs.USER_DEACTIVATED : EventLogs.USER_REACTIVATED,
                patches: patches,
                performedBy: updatedUser.updatedBy,
                performedAt: updatedUser.updatedAt,
                details: {
                    id: prev.id,
                    username: prev.username,
                },
            }, ctx);
        }
    }),
    delete: defineMutator(DeleteResourceSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'users.manage');
        const prev = await findById(tx, args.id);
        if (!prev)
            throw new Error('Not found');
        const now = Date.now();
        const updatedUser = {
            id: args.id,
            username: prev.username ? `${prev.username}_deleted_${now}` : null,
            deletedAt: now,
            deletedBy: ctx.user.id,
        };
        await tx.mutate.user.update(updatedUser);
        if (tx.location === 'server') {
            const patches = createPatch(prev, updatedUser);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: 'user',
                resourceId: args.id,
                domain: 'configuration',
                event: EventLogs.USER_DELETED,
                action: 'delete',
                patches: patches,
                performedBy: updatedUser.deletedBy,
                performedAt: updatedUser.deletedAt,
                details: {
                    id: prev.id,
                    username: prev.username,
                },
            }, ctx);
        }
    }),
};
//# sourceMappingURL=user.mutators.js.map